/**
 * USP bar – mobile auto-rotating carousel.
 *
 * On mobile (≤ 749px) one USP is visible at a time. Every `interval`
 * milliseconds the current item slides out to the left while the next
 * one slides in from the right, looping forever.
 *
 * On desktop the enhancement is torn down and all USPs render side-by-side
 * via CSS.
 *
 * Configuration (per section, on the root element):
 *   data-interval="3000"   Cycle duration in ms.
 */

const MOBILE_MQ = '(max-width: 749px)';
const SECTION_SELECTOR = '.js-uspbar';
const DEFAULT_INTERVAL_MS = 3000;

class UspBarCarousel {
    constructor(root) {
        this.root = root;
        this.track = root.querySelector('.uspbar__track');
        this.items = this.track ? Array.from(this.track.querySelectorAll('.uspbar__item')) : [];

        if (!this.track || this.items.length === 0) return;

        const parsed = parseInt(root.dataset.interval, 10);
        this.interval = Number.isFinite(parsed) && parsed > 0 ? parsed : DEFAULT_INTERVAL_MS;

        this.enabled = false;
        this.timer = null;
        this.index = 0;

        this.tick = this.tick.bind(this);
        this.onMqChange = this.onMqChange.bind(this);
        this.onVisibilityChange = this.onVisibilityChange.bind(this);

        this.mq = window.matchMedia(MOBILE_MQ);
        if (this.mq.addEventListener) {
            this.mq.addEventListener('change', this.onMqChange);
        } else {
            this.mq.addListener(this.onMqChange);
        }

        document.addEventListener('visibilitychange', this.onVisibilityChange);

        this.onMqChange();
    }

    onMqChange() {
        if (this.mq.matches && this.items.length > 1) {
            this.enable();
        } else {
            this.disable();
        }
    }

    onVisibilityChange() {
        if (!this.enabled) return;
        if (document.hidden) {
            this.stopTimer();
        } else {
            this.startTimer();
        }
    }

    enable() {
        if (this.enabled) return;
        this.enabled = true;

        this.index = 0;
        this.items.forEach((item, i) => {
            item.classList.remove('is-leaving');
            item.classList.toggle('is-active', i === 0);
        });

        this.startTimer();
    }

    disable() {
        if (!this.enabled) return;
        this.enabled = false;
        this.stopTimer();

        // Reset all state so the desktop CSS grid can render every item.
        this.items.forEach((item) => {
            item.classList.remove('is-active', 'is-leaving');
        });
    }

    startTimer() {
        this.stopTimer();
        this.timer = window.setInterval(this.tick, this.interval);
    }

    stopTimer() {
        if (this.timer !== null) {
            window.clearInterval(this.timer);
            this.timer = null;
        }
    }

    tick() {
        const current = this.items[this.index];
        const nextIndex = (this.index + 1) % this.items.length;
        const next = this.items[nextIndex];

        // Slide current out to the left.
        current.classList.remove('is-active');
        current.classList.add('is-leaving');

        // Slide next in from the right.
        next.classList.add('is-active');

        // Clean up the leaving class after the CSS transition finishes so
        // it's ready to enter from the right on its next turn.
        window.setTimeout(() => {
            current.classList.remove('is-leaving');
        }, 600);

        this.index = nextIndex;
    }

    destroy() {
        this.stopTimer();
        if (this.mq.removeEventListener) {
            this.mq.removeEventListener('change', this.onMqChange);
        } else {
            this.mq.removeListener(this.onMqChange);
        }
        document.removeEventListener('visibilitychange', this.onVisibilityChange);
    }
}

function init(scope = document) {
    scope.querySelectorAll(SECTION_SELECTOR).forEach((el) => {
        if (el.dataset.uspbarInit === 'true') return;
        el.dataset.uspbarInit = 'true';
        new UspBarCarousel(el);
    });
}

if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init());
} else {
    init();
}

// Re-init when a merchant edits the section in the Shopify theme editor.
document.addEventListener('shopify:section:load', (event) => {
    if (event.target) init(event.target);
});

